import { notify } from "@helpers"
import { Stripe, StripeElements } from "@stripe/stripe-js"
import { CardElement } from '@stripe/react-stripe-js'

export default function useActions(
    salon: Model.Response<Customer.Resources.Salon>, 
    api: Customer.Models.Salon.Api, 
    methods: Customer.Models.Salon.Methods
): Customer.Models.Salon.Actions {
    const participation = {
        store: getParticipationStoreAction(api, methods),
        destroy: getParticipationDestroyAction(api, methods)
    }

    const participate = getParticipateAction(api, methods)
    
    const payment = {
        capture: getPaymentCaptureAction(api, methods),
        stripe: getPaymentStripeAction(api, methods)
    }

    return {
        participate,
        participation,
        payment
    }
}

function getPaymentStripeAction(
    api: Customer.Models.Salon.Api, 
    methods: Customer.Models.Salon.Methods
) {
    return async (stripe: Stripe | null, elements: StripeElements | null, promo: Customer.States.Promo | undefined) => {
        if (!stripe || !elements)
            return

        const cardElement = elements.getElement(CardElement)

        if (!cardElement)
            return

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement
        })

        if (error) throw error

        if (!paymentMethod)
            return
        
        await api.payment.stripe(paymentMethod.id, promo?.code)
        
        notify("Thank you! We received the payment!")
        methods.paid()
    }
}

function getParticipationDestroyAction(
    api: Customer.Models.Salon.Api, 
    methods: Customer.Models.Salon.Methods
) {
    return async (participationId: number) => {
        const result = await api.participation.destroy(participationId)
        methods.participation.destroy(participationId)
        notify(result.message)
        return result.deleted
    }
}

function getPaymentCaptureAction(
    api: Customer.Models.Salon.Api, 
    methods: Customer.Models.Salon.Methods
) {
    return async (orderId: string, promo: string) => {
        await api.payment.capture(orderId, promo)
        notify("Thank you! We received the payment!")
        methods.paid()
    }
}

function getParticipationStoreAction(
    api: Customer.Models.Salon.Api, 
    methods: Customer.Models.Salon.Methods
): Customer.Models.Salon.Actions.Participation.Store {
    return async (photo: Interfaces.Photo, section: Customer.Resources.Section) => {
        const participation = await api.participation.store(section.id, photo.id)
        methods.participation.store(participation)
        return participation
    }
}

function getParticipateAction(
    api: Customer.Models.Salon.Api, 
    methods: Customer.Models.Salon.Methods
): Customer.Models.Salon.Actions.Participate {
    return async () => {
        await api.participate()
        notify("Thanks! You're participating in the salon!")
        methods.status.update("participating")
    }
}